import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { api, effectivePrice } from "@/lib/api";

const StoreContext = createContext(null);

const readLS = (key, fallback) => {
  try {
    const v = localStorage.getItem(key);
    return v ? JSON.parse(v) : fallback;
  } catch {
    return fallback;
  }
};

export function StoreProvider({ children }) {
  const [content, setContent] = useState(null);
  const [categories, setCategories] = useState([]);
  const [cart, setCart] = useState(() => readLS("art3d_cart", []));
  const [favorites, setFavorites] = useState(() => readLS("art3d_favs", []));
  const [cartOpen, setCartOpen] = useState(false);

  const refreshContent = useCallback(async () => {
    try {
      const { data } = await api.get("/content");
      setContent(data);
    } catch (e) {
      console.error("content load failed", e);
    }
  }, []);

  const refreshCategories = useCallback(async () => {
    try {
      const { data } = await api.get("/categories");
      setCategories(data);
    } catch (e) {
      console.error("categories load failed", e);
    }
  }, []);

  useEffect(() => {
    refreshContent();
    refreshCategories();
  }, [refreshContent, refreshCategories]);

  useEffect(() => localStorage.setItem("art3d_cart", JSON.stringify(cart)), [cart]);
  useEffect(() => localStorage.setItem("art3d_favs", JSON.stringify(favorites)), [favorites]);

  const cartKey = (productId, color, variation) => `${productId}|${color || ""}|${variation || ""}`;

  const addToCart = useCallback((product, qty = 1, color = "", variation = "") => {
    const key = cartKey(product.id, color, variation);
    setCart((prev) => {
      const idx = prev.findIndex((i) => i.key === key);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], qty: next[idx].qty + qty };
        return next;
      }
      return [
        ...prev,
        {
          key,
          product_id: product.id,
          name: product.name,
          price: effectivePrice(product),
          image: product.images?.[0] || "",
          color,
          variation,
          qty,
        },
      ];
    });
    setCartOpen(true);
  }, []);

  const updateQty = useCallback((key, qty) => {
    setCart((prev) =>
      qty <= 0 ? prev.filter((i) => i.key !== key) : prev.map((i) => (i.key === key ? { ...i, qty } : i))
    );
  }, []);

  const removeFromCart = useCallback((key) => {
    setCart((prev) => prev.filter((i) => i.key !== key));
  }, []);

  const clearCart = useCallback(() => setCart([]), []);

  const toggleFavorite = useCallback((id) => {
    setFavorites((prev) => (prev.includes(id) ? prev.filter((f) => f !== id) : [...prev, id]));
  }, []);

  const cartCount = useMemo(() => cart.reduce((s, i) => s + i.qty, 0), [cart]);
  const cartTotal = useMemo(() => cart.reduce((s, i) => s + i.price * i.qty, 0), [cart]);

  const value = {
    content,
    categories,
    refreshContent,
    refreshCategories,
    cart,
    cartCount,
    cartTotal,
    addToCart,
    updateQty,
    removeFromCart,
    clearCart,
    cartOpen,
    setCartOpen,
    favorites,
    toggleFavorite,
  };

  return <StoreContext.Provider value={value}>{children}</StoreContext.Provider>;
}

export const useStore = () => useContext(StoreContext);
